// ============================================================
// How the map is drawn - in Build on the option bar, elsewhere below it
// ============================================================

import clsx from '../../utils/clsx';
import common from '../../styles/common.module.css';
import styles from '../../styles/Battlefield.module.css';
import MapIcon from '../MapIcon';
import { TERRAIN_ANIMATIONS } from '../../engine/battlefield/terrainAnimation';
import { MARKER_STATUS_LEVELS } from '../../engine/battlefield/markerStatus';

/** One on/off switch of the view, styled as a mode button. */
function ViewToggle({ testId, on, onClick, title, children }) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={on}
      className={clsx(styles.mode, on && styles.modeActive)}
      onClick={onClick}
      title={title}
    >
      {children}
    </button>
  );
}

/** One picker of the view: a list of `{ key, label, hint }` with the hint of the chosen one as its tooltip. */
function ViewPicker({ label, testId, value, onPick, entries }) {
  return (
    <select
      className={styles.picker}
      aria-label={label}
      data-testid={testId}
      value={value}
      onChange={(e) => onPick(e.target.value)}
      title={entries.find((entry) => entry.key === value)?.hint}
    >
      {entries.map((entry) => (
        <option key={entry.key} value={entry.key}>
          {entry.label}
        </option>
      ))}
    </select>
  );
}

/**
 * THE ONE SESSION RULING ON THIS BAR, here because a GM reaches for it
 * while looking at the map. It sat among the private preferences until
 * v8.63.0, styled like them and ungated, so any player could switch the
 * geometry off for the whole table. Its home is Settings › Battlefield;
 * this is a shortcut, and only a GM has it (`onIgnoreTerrainChange`).
 */
function TerrainRuling({ view }) {
  if (!view.onIgnoreTerrainChange) return null;
  return (
    <ViewToggle
      testId="battlefield-terrain-off"
      on={view.ignoreTerrain}
      onClick={() => view.onIgnoreTerrainChange(!view.ignoreTerrain)}
      title="Ignore terrain for cover and line of sight - a ruling for the whole table"
    >
      <MapIcon name="noTerrain" /> Terrain
    </ViewToggle>
  );
}

/** The camera's preset angles, and the way back. */
function CameraButtons({ view }) {
  return (
    <>
      {view.cameraPresets?.map((p) => (
        <button
          key={p.key}
          type="button"
          data-testid={`battlefield-view-${p.key}`}
          className={styles.mode}
          onClick={() => view.applyPreset(p)}
        >
          {p.label}
        </button>
      ))}
      <button type="button" className={common.btnSmall} data-testid="battlefield-reset-view" onClick={view.resetCamera}>
        <MapIcon name="reset" /> Reset View
      </button>
    </>
  );
}

/** The view's on/off switches. The detail switch only exists once Inspect is on: a detail switch with nothing to add detail to is a puzzle. */
function ViewSwitches({ view }) {
  return (
    <>
      <ViewToggle
        testId="battlefield-labels"
        on={view.showLabels}
        onClick={view.toggleLabels}
        title="Show combatant names beside their markers"
      >
        <MapIcon name="labels" /> Names
      </ViewToggle>
      <TerrainRuling view={view} />
      <ViewToggle
        testId="battlefield-autotarget"
        on={view.autoTarget}
        onClick={view.toggleAutoTarget}
        title="Measuring at a combatant makes them your target"
      >
        <MapIcon name="target" /> Auto-target
      </ViewToggle>
      <ViewToggle
        testId="battlefield-hoverinfo"
        on={view.hoverInfo}
        onClick={view.toggleHoverInfo}
        title="Hovering says what a structure or surface is, and what it does"
      >
        <MapIcon name="legend" /> Inspect
      </ViewToggle>
      {view.hoverInfo && (
        <ViewToggle
          testId="battlefield-hoverdetail"
          on={view.hoverDetail}
          onClick={view.toggleHoverDetail}
          title="Also give the deeper structural facts - elevation, raw values, what movement makes of it"
        >
          + Detail
        </ViewToggle>
      )}
      <ViewToggle
        testId="battlefield-showpings"
        on={view.showPings}
        onClick={view.toggleShowPings}
        title="Show pings from the table"
      >
        Pings
      </ViewToggle>
      <ViewToggle
        testId="battlefield-terrainmarks"
        on={view.terrainMarks}
        onClick={view.toggleTerrainMarks}
        title="Hatch dangerous and impassable ground"
      >
        Marks
      </ViewToggle>
    </>
  );
}

/** What the map SHOWS: the switches and the two pickers. */
export function ShowOptions({ view }) {
  return (
    <>
      <ViewSwitches view={view} />
      <ViewPicker
        label="Terrain animation"
        testId="battlefield-animate"
        value={view.terrainAnimation}
        onPick={view.setTerrainAnimation}
        entries={TERRAIN_ANIMATIONS}
      />
      <ViewPicker
        label="Marker status"
        testId="battlefield-markerstatus"
        value={view.markerStatus}
        onPick={view.setMarkerStatus}
        entries={MARKER_STATUS_LEVELS}
      />
    </>
  );
}

/** What is switched on, in words - the Show menu's closed label. */
export function shownSummary(view) {
  const on = [
    view.showLabels && 'names',
    view.ignoreTerrain && 'terrain ignored',
    view.hoverInfo && 'inspect',
    view.showPings && 'pings',
    view.terrainMarks && 'marks',
  ].filter(Boolean);
  return on.length ? on.join(', ') : 'nothing extra';
}

/**
 * How the map is drawn - the same for a GM building and a player
 * fighting. In Build it is an item on the option bar, all on one row; in
 * Play and Deploy the toolbar folds it into two menus (PanelToolbar).
 */
export default function ViewOptions({ view }) {
  if (!view) return null;
  return (
    <>
      <ShowOptions view={view} />
      <span className={styles.modifierDivider} aria-hidden="true" />
      <CameraButtons view={view} />
    </>
  );
}

export { CameraButtons as CameraOptions };
